// ── Airport display formatting (RD-3960) ─────────────────────────────────────
//
// Renders an airport as `{Airport name} ({IATA code})`, e.g.
// "Changi Airport Singapore (SIN)", from the Google-Places object stored on a
// VIP M&G rate card / flight info.
//
// ⚠️ THREE COPIES — keep in step:
//   rolzo-web/src/helpers/airportDisplay.js
//   rolzo-api/imports/api/shared/airportDisplay.js
//   rolzo_ls_crons_events/helpers/airportDisplay.js
//
// WHY THIS EXISTS
// There is no stored "airport name" field. The admin form labels the rate card's
// `airport` field "Airport name*" but binds it to Google's `fullAddress`
// (AddRate.jsx, displayKey: 'fullAddress'), and the place sub-schema
// (globalRates/schema.js) has no `name` field at all. The name therefore has to
// be derived from the formatted address every time it is displayed.
//
// The previous derivation, copy-pasted in 8 places, was:
//     airport.includes(',') ? airport.split(',')[0] : airport.split('-')[0]
// Taking the first comma-segment assumes Google puts the airport name first. It
// does for Madrid ("Aeropuerto ... Madrid-Barajas (MAD), Avenida ...") but not
// for Changi ("Airport Boulevard, Changi Airport Singapore (SIN)"), which is
// why Changi rendered as "Airport Boulevard". The `split('-')` fallback also
// truncated comma-less hyphenated names ("Madrid-Barajas" -> "Madrid").
//
// Instead we pick the comma-segment that actually contains the airport's IATA
// code in parentheses. No per-airport hardcoding (RD-3960 forbids it).

// A parenthesised 3-letter code, e.g. "(SIN)". Mirrors the anchor used by
// extractIataCode in rolzo-web/src/components/FlightTrackingNew/flightNerveResolver.js
const IATA_IN_PARENS = /\(\s*([A-Z]{3})\s*\)/

// Trailing parenthesised code, stripped before we re-append the code ourselves
// so a source that already carries it does not end up "... (MAD) (MAD)".
const TRAILING_IATA = /\s*\(\s*[A-Z]{3}\s*\)\s*$/

// Words that mark a segment as the airport itself, across the languages Google
// returns for the airports on our rate cards.
const AIRPORT_WORD = /a[ée]roport|aeroporto|aeropuerto|flughafen|lotnisko|airport|luchthaven|havalimani|havalimanı/i

// Street-type words, matched whole-word so "John Wayne" is not caught by "way".
// Needed because Changi's first segment is "Airport Boulevard" — it contains
// "Airport" but is a road, not the airport.
const STREET_WORD = /\b(boulevard|blvd|road|rd|street|st|avenue|ave|drive|dr|lane|ln|way|highway|hwy|route|via|rue|calle|strasse|straße|weg)\b/i

const text = value => String(value == null ? '' : value).trim()

// A place-shaped object: the rate card / flightInfo nests it under `airport`,
// while pickUpLocation / dropOffLocation ARE one.
const toPlace = source => {
  if (!source || typeof source !== 'object') return null
  if (source?.airport && typeof source.airport === 'object')
    return source.airport
  return source
}

const isIataCode = value => /^[A-Z]{3}$/.test(text(value).toUpperCase())

const normaliseIata = value => {
  const raw = text(value).toUpperCase()
  return isIataCode(raw) ? raw : ''
}

// Resolve the IATA code, most authoritative first.
//
// This chain is lifted from bookings/server/helpers.js (the working travel-doc
// prompt), which already resolves IATA exactly this way. The flightInfo
// fallback is not optional: production bookings exist with no `place.iata` at
// all (a Haneda booking carried only arrivalFlightInfo.iata_code), and without
// it those render "Haneda Airport ()".
export const resolveAirportIataCode = (source, flightInfo) => {
  try {
    const place = toPlace(source)

    const candidates = [
      // Admin-typed rate-card field — authoritative when present.
      source?.iata_code,
      place?.iata,
      place?.iata_code,
      flightInfo?.iata_code,
      flightInfo?.airport?.iata,
    ]

    for (let i = 0; i < candidates.length; i += 1) {
      const code = normaliseIata(candidates[i])
      if (code) return code
    }

    // Last resort: the address itself often embeds the code, e.g.
    // "... Madrid-Barajas (MAD), ...".
    const embedded = text(place?.fullAddress).match(IATA_IN_PARENS)
    return embedded ? embedded[1] : ''
  } catch (error) {
    return ''
  }
}

// Choose the comma-segment that names the airport.
const pickNameSegment = (fullAddress, iata) => {
  const segments = text(fullAddress)
    .split(',')
    .map(segment => segment.trim())
    .filter(Boolean)

  if (!segments.length) return ''

  // 1. The segment carrying this airport's own code — the reliable signal.
  if (iata) {
    const withCode = segments.find(segment =>
      new RegExp('\\(\\s*' + iata + '\\s*\\)', 'i').test(segment)
    )
    if (withCode) return withCode
  }

  // 2. A segment naming an airport that is not a street.
  const airportish = segments.find(
    segment => AIRPORT_WORD.test(segment) && !STREET_WORD.test(segment)
  )
  if (airportish) return airportish

  // 3. Any segment carrying some parenthesised code.
  const withAnyCode = segments.find(segment => IATA_IN_PARENS.test(segment))
  if (withAnyCode) return withAnyCode

  // 4. Legacy behaviour: the first segment. Note we never split on '-', which
  //    used to truncate "Madrid-Barajas" to "Madrid".
  return segments[0]
}

// The airport's display name, WITHOUT the code appended.
export const formatAirportName = (source, flightInfo) => {
  try {
    const place = toPlace(source)
    if (!place) return ''

    const iata = resolveAirportIataCode(source, flightInfo)
    const name = text(place?.fullAddress)
      ? text(pickNameSegment(place.fullAddress, iata))
          .replace(TRAILING_IATA, '')
          .trim()
      : ''

    return name || text(place?.city) || ''
  } catch (error) {
    return ''
  }
}

// The code to DISPLAY next to the name — which is not always the code the
// explicit fields claim.
//
// A sibling `iata_code` can be STALE while the nested `airport` is current.
// Real case, booking 9398-R: a change request moved the booking from Fiumicino
// to Ciampino. `airport.fullAddress` became "Ciampino–G.B. Pastine
// International Airport (CIA), ..." but `arrivalFlightInfo.iata_code` was left
// as "FCO", so the explicit chain rendered
//     "Ciampino–G.B. Pastine International Airport (FCO)"
// — a name and a code naming two different airports.
//
// Rule: the code we show MUST be the one embedded in the name we show. Only
// when the displayed name carries no code do we trust the explicit fields.
// This makes a name/code contradiction structurally impossible.
export const resolveDisplayIata = (source, flightInfo) => {
  try {
    const place = toPlace(source)
    const claimed = resolveAirportIataCode(source, flightInfo)

    const segment = text(place?.fullAddress)
      ? pickNameSegment(place.fullAddress, claimed)
      : ''
    const embedded = text(segment).match(IATA_IN_PARENS)

    return embedded ? embedded[1] : claimed
  } catch (error) {
    return ''
  }
}

// `{Airport name} ({IATA code})` — the RD-3960 standard format.
//
// Degrades safely: name-only when no code resolves (never an empty "()"),
// code-only when no name resolves, '' when neither. These run inside Blaze
// template helpers and PDF generators where a throw is silently swallowed and
// drops whole sections, so this must never throw.
export const formatAirportDisplay = (source, flightInfo) => {
  try {
    const iata = resolveDisplayIata(source, flightInfo)
    const name = formatAirportName(source, flightInfo)

    if (name && iata) return name + ' (' + iata + ')'
    if (name) return name
    return iata || ''
  } catch (error) {
    return ''
  }
}

// IATA code alone, for travel-document / passenger-information prompts:
//   "To ensure a smooth experience at {IATA} Airport, ..."
// Falls back to the display name so the sentence stays grammatical when no code
// can be resolved.
export const formatAirportIataOnly = (source, flightInfo) => {
  try {
    // resolveDisplayIata, not resolveAirportIataCode: a prompt saying "smooth
    // experience at FCO Airport" for a booking at CIA is the same defect.
    return (
      resolveDisplayIata(source, flightInfo) ||
      formatAirportName(source, flightInfo) ||
      ''
    )
  } catch (error) {
    return ''
  }
}

// ── Email subject lines (RD-3960) ────────────────────────────────────────────
//
// Subjects end in a location: `... | Thu, 10th Sep 2026, 09:00 | Ciampino, IT`.
// For a VIP M&G booking that location IS the airport, so it must use the
// standard format. Two live defects this fixes:
//   · Changi rendered "Airport Boulevard, SG" — its stored `city` is the street.
//   · Heathrow rendered "Longford, GB" — the village, not the airport.
//
// Non-VIP bookings are untouched: a hotel/address pick-up is still described by
// its city, and the US `city, state` convention is preserved exactly.
// Which stored location is the airport. Add-on arrivals keep it in
// pickUpLocation; add-on DEPARTURES keep it in dropOffLocation (the pick-up is
// the hotel) — QA 5413-V rendered "London, GB" on every subject because only
// pickUpLocation was consulted. `locationType` is written as the string
// 'isAirport' by the booking forms but as a bare boolean by the autocomplete
// components, so accept both, then fall back to "the address itself carries an
// IATA code" (no flight info consulted here — a hotel pick-up on a flight
// booking must not be promoted to an airport by the flight's code).
const isAirportLocation = loc => {
  if (!loc) return false
  if (loc.locationType === 'isAirport' || loc.locationType === true) return true
  return !!resolveDisplayIata(loc)
}

export const formatSubjectLocation = (booking, location) => {
  try {
    // Add-on M&G bookings are flagged by meetAndAssistId once the M&G record
    // exists, but confirmation-time emails and projected reads can see only
    // the embedded meetAndAssist / meetAndGreet objects — QA 5412-V.
    const isVipMg = !!(
      booking?.isStandaloneVip ||
      booking?.meetAndAssistId ||
      booking?.meetAndAssist ||
      booking?.meetAndGreet ||
      booking?.vipServiceType ||
      booking?.type === 'vip_meet_greet'
    )

    const pickUp = booking?.pickUpLocation
    const dropOff = booking?.dropOffLocation
    // An explicit `location` (a caller naming the leg's airport) decides which
    // flight info feeds the IATA fallback; otherwise pick-up wins, drop-off only
    // when the pick-up is not an airport.
    const useDropOff = location
      ? location === dropOff
      : isVipMg && !isAirportLocation(pickUp) && isAirportLocation(dropOff)
    const loc = location || (useDropOff ? dropOff : pickUp)
    if (!loc) return ''

    if (isVipMg && isAirportLocation(loc)) {
      const flightInfo = useDropOff
        ? booking?.departureFlightInfo ||
          booking?.meetAndAssist?.departureInfo ||
          booking?.arrivalFlightInfo
        : booking?.arrivalFlightInfo ||
          booking?.meetAndAssist?.arrivalInfo ||
          booking?.departureFlightInfo
      const airport = formatAirportDisplay(loc, flightInfo)
      if (airport) return airport
    }

    // Unchanged legacy behaviour.
    const region =
      loc?.country === 'US' ? loc?.state : loc?.country || loc?.state
    return [text(loc?.city), text(region)].filter(Boolean).join(', ')
  } catch (error) {
    return ''
  }
}
